import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    effect,
    inject,
    signal,
    viewChild
} from '@angular/core';
import {IntersectionObserverDirective} from '../../directives/intersection-observer.directive';
import {WjFlexGrid, WjGridModule} from '@mescius/wijmo.angular2.grid';
import {ColumnGroupDeclaration, Wijmo, WjGridUtils} from '../../lib/wjgrid-utils';
import {DashboardTargetsMainComponent} from '../dashboard-targets-main/dashboard-targets-main.component';
import {CompactSummaryTables} from '../../lib/compact-summary-tables';
import {WjChartModule} from '@mescius/wijmo.angular2.chart';
import {WjNavModule} from '@mescius/wijmo.angular2.nav';
import {WjChartInteractionModule} from '@mescius/wijmo.angular2.chart.interaction';
import {WjChartAnnotationModule} from '@mescius/wijmo.angular2.chart.annotation';
import {WjChartAnalyticsModule, WjFlexChartWaterfall} from '@mescius/wijmo.angular2.chart.analytics';
import {WijmoFormatPipe} from '../../pipes/wijmo-format.pipe';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';
import {
    PfReportDataHelper,
    PfTargetUwySummaryRow,
    UwStrategyBucketKeyAccount,
    UwStrategyBucketNewBusiness,
    UwStrategyBucketNotRenewable,
    UwStrategyBucketTotal,
    UwStrategyBucketTotalExNewBusiness,
} from '../../data/portfolio-segment-report-data';
import {ExcelFmtEditable, ExcelFmtEditableNotAllowed, ExcelRowFormat} from '../../lib/wjgrid-customcells';
import {Globalize} from '@mescius/wijmo';
import {CellRangeEventArgs, FlexGrid, FormatItemEventArgs} from '@mescius/wijmo.grid';
import {AccessControlDirective} from '../../directives/access-control.directive';
import {HitTestInfo, IRenderEngine} from '@mescius/wijmo.chart';
import {Stdlib} from '../../lib/stdlib';
import {AccessControlService} from '../../security/access-control.service';
import {AppConstants} from '../../app.constants';
import {WaterfallChartDataItem, WjCharts} from '../../lib/wjcharts';

const rdCol = CompactSummaryTables.rdCol;

/**
 * The `PfReportTargetsComponent` class is an implementation of the Excel Worksheet "Portfolio Targets Report".
 *
 * Key Features:
 * - Provides financial summaries for different groupings.
 * - Displays interactive waterfall charts for financial year and underwriting year summaries.
 *
 * Data Summaries and Charts:
 * - **Grids**:
 *   - `gridUwySummary`: Displays underwriting year summaries.
 *   - `gridFinancialYearSummary`: Displays financial year summaries.
 *   - `gridLlSummary`: Displays control accounts portfolio summaries.
 * - **Charts**:
 *   - `uwyWaterfallChart`: Waterfall chart illustrating underwriting year data.
 *   - `fyWaterfallChart`: Waterfall chart summarizing financial year data.
 */
@Component({
    selector: 'app-pf-report-targets',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        IntersectionObserverDirective,
        WjGridModule,
        WjChartModule,
        WjChartAnnotationModule, WjChartInteractionModule, WjNavModule, WjChartAnalyticsModule, WijmoFormatPipe, AccessControlDirective,
    ],
    templateUrl: './pf-report-targets.component.html',
    styleUrl: './pf-report-targets.component.scss'
})
export class PfReportTargetsComponent implements AfterViewInit {

    protected readonly Wijmo = Wijmo;
    protected readonly GeneraliLossRatioWaterfallChartStyle = WjCharts.GeneraliLossRatioWaterfallChartStyle;
    protected readonly GeneraliLossRatioWaterfallChartDeltaLabelsFunction = WjCharts.waterfallChartDeltaLabelsFunction;

    protected readonly pfSegData = inject(PortfolioSegmentDataContext);

    private gridUwySummary = viewChild<WjFlexGrid>('gridUwySummary');
    private gridFinancialYearSummary = viewChild<WjFlexGrid>('gridFinancialYearSummary');
    private gridLlSummary = viewChild<WjFlexGrid>('gridLlSummary');

    private uwyWaterfallChart = viewChild<WjFlexChartWaterfall>('uwyWaterfallChart');
    private fyWaterfallChart = viewChild<WjFlexChartWaterfall>('fyWaterfallChart');

    protected readonly grandTotalGnwpPlan = signal(0)
    protected readonly grandTotalGnwpInScope = signal(0)
    protected readonly grandTotalGnwpTarget = signal(0)
    protected readonly grandTotalNlrThisUwy = signal(0)
    protected readonly grandTotalPlr = signal(0)

    protected readonly uwy = computed(() => this.pfSegData.portfolio.value()?.uwyear);

    private readonly accessControlSvc = inject(AccessControlService);

    constructor() {
        effect(async () => {
            await this.prepareReportData();
            await this.prepareGrandTotals()
            await this.prepareWaterfallChartData();
            await this.prepareWaterfallChartDataFy();
        });
    }

    async ngAfterViewInit(): Promise<void> {
        const chart = this.uwyWaterfallChart()!;
        await this.prepareReportData();
    }

    private async classificationTiersHighestFirst(): Promise<string[]> {
        await this.pfSegData.waitForResourcesToLoad();
        return this.pfSegData.classificationTierSchema.value()!.tiersNamesHighestFirst();
    }

    private editableIfGranted() {
        const canEditSegment = this.accessControlSvc.canEditSegmentCtx(this.pfSegData).allowed;
        return canEditSegment ? ExcelFmtEditable : ExcelFmtEditableNotAllowed;
    }

    // ===========================  Table "UW Year Summary"  ================================
    async onUwySummaryGridInitialized(grid: WjFlexGrid) {
        const uwy = this.pfSegData.portfolio.value()?.uwyear;
        const columns: ColumnGroupDeclaration[] = [
            { header: ' ', binding: 'bucket', width: 230, height: 100, align: 'center', cssClass: 'bold-text' },
            { header: 'Expiring Portfolio', align: 'center', columns: [
                    { header: 'Policy Count', align: 'center', columns: [
                            rdCol('#', 'count', 'n0'),
                            rdCol('%', 'countPercent', 'p1')
                        ]
                    },
                    { header: 'Portfolio Composition', align: 'center', columns: [
                            rdCol('GNWP, m EUR', 'gnwpEur', 'n1,,'),
                            rdCol(`${uwy} NLR`, 'nlr', 'p1')
                        ]
                    }
                ]
            },
            { header: 'Portfolio Targets', align: 'center', columns: [
                    rdCol('Target Rate Chg', 'targetRateChange', 'p1'),
                    rdCol('Target Retention Rate', 'targetRetentionRate', 'p1')
                ]
            },
            { header: 'New Portfolio', align: 'center', columns: [
                    rdCol('GNWP, m EUR', 'renewalGnwpEur', 'n1,,'),
                    rdCol('Claims, m EUR', 'renewalClaimsEur', 'n1,,'),
                    rdCol('New NLR (%)', 'renewalNlr', 'p1')
                ]
            },
        ];

        WjGridUtils.addColumnGroups(grid, columns);
        WjGridUtils.setHeaderRowHeights(grid, 20, 40, 50);
        WjGridUtils.addCellValueChangeHandler(grid, this.onUwySummaryGridCellValueChanged.bind(this));

        DashboardTargetsMainComponent.decorateGridWithDashboardDefaultBehaviour(grid);
    }

    private async onUwySummaryGridCellValueChanged(s: FlexGrid, e: CellRangeEventArgs) {
        const data = e.getRow().dataItem as PfTargetUwySummaryRow;
        const col = e.getColumn();
        if (data.bucket === UwStrategyBucketNewBusiness) {
            if (col.binding === 'renewalGnwpEur') {
                const newBusinessGnwpEur = data.renewalGnwpEur ?? 0;
                this.pfSegData.setParameter(AppConstants.PYFORMULA_NEW_BUSINESS_GNWP_EUR_TARGET, newBusinessGnwpEur);
            } else if (col.binding === 'nlr') {
                const newBusinessGnwpEur = data.nlr ?? 0;
                this.pfSegData.setParameter(AppConstants.PYFORMULA_NEW_BUSINESS_NLR_TARGET, newBusinessGnwpEur);
            }
            this.pfSegData.updateFormulasAndValidations();
        }
    }

    async onFinancialYearSummaryGridInitialized(grid: WjFlexGrid) {
        const uwy = this.pfSegData.portfolio.value()?.uwyear;

        const columns: ColumnGroupDeclaration[] = [
            { header: ' ', binding: 'bucket', width: 230, align: 'center', cssClass: 'bold-text' },
            { header: `${uwy} Financial Year`, align: 'center', columns: [
                    rdCol('GNEP, m EUR', 'fyGnep', 'n1,,'),
                    rdCol('Earned Rate Change', 'fyEarnedRateChange', 'p1'),
                    rdCol(`NLR (FY ${uwy})`, 'fyNlr', 'p1'),
                    rdCol(`Plan LR (FY ${uwy})`, 'fyNlr', 'p1'),
                    rdCol('Delta to Plan', 'fyPlrDelta', 'p1')
                ]
            },
        ];

        WjGridUtils.addColumnGroups(grid, columns);
        WjGridUtils.setHeaderRowHeights(grid, 20, 90);
        DashboardTargetsMainComponent.decorateGridWithDashboardDefaultBehaviour(grid);

        grid.formatItem.addHandler(this.accountCellsFormatHandler.bind(this));
    }

    async onLLSummaryGridInitialized(grid: WjFlexGrid) {
        const columns: ColumnGroupDeclaration[] = [
            { header: ' ', binding: 'bucket', width: 230, align: 'center', cssClass: 'bold-text' },
            rdCol('Total Expiring GNWP','gnwpEur', 'n1,,'),
            { header: 'Control Accounts - Expiring Portfolio', align: 'center', columns: [
                    rdCol('#', 'llCount', 'g0'),
                    rdCol('%', 'llPct', 'p1'),
                    rdCol('GNWP', 'llGnwpEur', 'n1,,'),
                    rdCol('Expected Nr. of LL per year', 'llExpectedLlPerYear', 'n3'),
                    rdCol('Intensity (%)', 'llIntensity', 'p2')
                ]
            },
        ];

        WjGridUtils.addColumnGroups(grid, columns);
        WjGridUtils.setHeaderRowHeights(grid, 20, 90);
        DashboardTargetsMainComponent.decorateGridWithDashboardDefaultBehaviour(grid);
    }

    private accountCellsFormatHandler(s: FlexGrid, e: FormatItemEventArgs) {
        if (e.panel === s.rowHeaders) {
        } else if (e.panel === s.cells) {
            const cell = e.cell;
            const item = s.collectionView.items[e.row] as PfTargetUwySummaryRow;
            const column = s.columns[e.col];
            if (item.bucket === UwStrategyBucketTotal && column.binding === 'fyPlrDelta') {
                const plrDeltaToPlan = item.fyPlrDelta ?? 0;
                if (plrDeltaToPlan < 0) {
                    cell.classList.add('plr-delta-good');
                } else if (plrDeltaToPlan < 0.1) {
                    cell.classList.add('plr-delta-fair');
                } else {
                    cell.classList.add('plr-delta-poor');
                }
            }
        }
    }

    private async prepareReportData() {
        Stdlib.touchSignals(this.pfSegData.wb.uwySummary);
        await this.pfSegData.waitForResourcesToLoad();
        const summary = structuredClone(this.pfSegData.wb.uwySummary());

        // insert a subtotal row before non-renewable
        const newBusinessIdx = PfReportDataHelper.findSummaryRowIndex(summary, UwStrategyBucketNewBusiness);
        const newBusinessRow = summary[newBusinessIdx] as any;
        if (newBusinessRow) {
            newBusinessRow.renewalGnwpEurCellFmt = this.editableIfGranted();
            newBusinessRow.nlrCellFmt = this.editableIfGranted();
        }

        if (newBusinessIdx > 0) {
            const rowFmt: ExcelRowFormat = { cssClass: 'white-on-white', height: 8, isReadOnly: true };
            const separatorRow: any  = { bucket: '', rowFmt };
            summary.splice(newBusinessIdx, 0, separatorRow);
        }
        //this.reportData.set(summary);
        WjGridUtils.updateGridItemsSourceAndPreserveSelection(this.gridUwySummary(), summary);
        WjGridUtils.updateGridItemsSourceAndPreserveSelection(this.gridFinancialYearSummary(), summary);
        WjGridUtils.updateGridItemsSourceAndPreserveSelection(this.gridLlSummary(), summary);
    }

    private async prepareGrandTotals() {
        await this.pfSegData.waitForResourcesToLoad();
        const summary = structuredClone(this.pfSegData.wb.uwySummary());
        const totalExNb = PfReportDataHelper.findSummaryRow(summary, UwStrategyBucketTotalExNewBusiness);
        const total = PfReportDataHelper.findSummaryRow(summary, UwStrategyBucketTotal);
        this.grandTotalGnwpPlan.set(this.pfSegData.wb.configMatrixValue('gnwp') ?? 0);
        this.grandTotalGnwpInScope.set(totalExNb?.gnwpEur ?? 0);
        this.grandTotalGnwpTarget.set(total?.renewalGnwpEur ?? 0);

        this.grandTotalNlrThisUwy.set(this.pfSegData.wb.configMatrixValue('nlr') ?? 0);
        this.grandTotalPlr.set(this.pfSegData.wb.configMatrixValue('plr') ?? 0);
    }

    protected async prepareWaterfallChartData() {
        const chart = this.uwyWaterfallChart();
        const summary = structuredClone(this.pfSegData.wb.uwySummary());

        const totalExNbRow = summary.find(row => row.bucket === UwStrategyBucketTotalExNewBusiness);
        const totalRow = summary.find(row => row.bucket === UwStrategyBucketTotal);

        if (totalRow && totalExNbRow && chart) {
            const uwy = this.pfSegData.wb.uwy;
            const nlrUwy = nv(totalExNbRow.nlr);
            const chartData: WaterfallChartDataItem[] = [ { name: `${uwy} NLR`, value: nlrUwy }];

            function nv(v: number | undefined) { return v ?? 0; }

            let gnwp = nv(totalExNbRow.gnwpEur);
            let loss = gnwp * nlrUwy;

            const buckets = await this.classificationTiersHighestFirst();
            buckets.push(UwStrategyBucketKeyAccount, UwStrategyBucketNotRenewable);
            buckets.forEach(buckets => {
                const row = PfReportDataHelper.findSummaryRow(summary, buckets)!;
                gnwp += -nv(row.gnwpEur) + nv(row.renewalGnwpEur);
                loss -= nv(row.gnwpEur) * nv(row.nlr) - nv(row.renewalClaimsEur);
                const lr = loss / gnwp;

                chartData.push({ name: buckets, value: lr });
            });

            const totals = [{ position: chartData.length, label: UwStrategyBucketTotalExNewBusiness }];

            // 'new business' entry - is strangely based on total ...
            chartData.push({
                name: UwStrategyBucketNewBusiness,
                value: nv(totalRow.renewalClaimsEur) / nv(totalRow.renewalGnwpEur)
            });
            totals.push({ position: chartData.length, label: 'Target NLR' });

            chartData.push({
                name: 'Delta to Plan',
                value: nv(this.pfSegData.wb.configMatrixValue('plr'))
            });
            totals.push({ position: chartData.length, label: 'Plan LR' });

            WjCharts.fillWaterfallChartDeltaLabels(chartData);

            chart.chart.itemsSource = chartData;
            chart.intermediateTotalPositions = totals.map(it => it.position);
            chart.intermediateTotalLabels = totals.map(it => it.label);
            chart.showIntermediateTotal = true;
            chart.totalLabel = '**Total';
            chart.tooltipContent = (hti: HitTestInfo) => `${hti.x}: ${Globalize.format(hti.y, 'p1')}`
        }
    }

    protected async prepareWaterfallChartDataFy() {
        const chart = this.fyWaterfallChart();
        const summary = structuredClone(this.pfSegData.wb.uwySummary());

        const totalExNbRow = summary.find(row => row.bucket === UwStrategyBucketTotalExNewBusiness);
        const totalRow = summary.find(row => row.bucket === UwStrategyBucketTotal);

        if (totalRow && totalExNbRow && chart) {
            const uwy = this.pfSegData.wb.uwy;
            const nlrFy = this.grandTotalNlrThisUwy();
            const chartData: WaterfallChartDataItem[] = [ { name: `${uwy} NLR`, value: nlrFy }];

            function nv(v: number | undefined) { return v ?? 0; }

            let gnwp = nv(totalExNbRow.gnwpEur);
            let loss = gnwp * nlrFy;

            const buckets = await this.classificationTiersHighestFirst();
            buckets.push(UwStrategyBucketKeyAccount, UwStrategyBucketNotRenewable);
            buckets.forEach(buckets => {
                const row = PfReportDataHelper.findSummaryRow(summary, buckets)!;
                gnwp -= nv(row.fyGnwpEarnedInUwy);
                loss -= nv(row.fyGnwpEarnedInUwy) * nv(row.nlr); // - nv(row.fyNlr) * nv(row.fyGnep);
                const lr = loss / gnwp;

                chartData.push({ name: buckets, value: lr });
            });

            const totals = [{ position: chartData.length, label: UwStrategyBucketTotalExNewBusiness }];

            // 'new business' entry - is strangley based on total ...
            chartData.push({
                name: UwStrategyBucketNewBusiness,
                value: nv(totalRow.fyNlr)
            });
            totals.push({ position: chartData.length, label: 'Target NLR' });

            chartData.push({
                name: 'Delta to Plan',
                value: nv(this.pfSegData.wb.configMatrixValue('plr'))
            });
            totals.push({ position: chartData.length, label: 'Plan LR' });

            WjCharts.fillWaterfallChartDeltaLabels(chartData);

            chart.chart.itemsSource = chartData;
            chart.intermediateTotalPositions = totals.map(it => it.position);
            chart.intermediateTotalLabels = totals.map(it => it.label);
            chart.showIntermediateTotal = true;
            chart.totalLabel = '**Total';
            chart.tooltipContent = (hti: HitTestInfo) => `${hti.x}: ${Globalize.format(hti.y, 'p1')}`

        }
    }

    protected WaterfallChartPaletteItemFormatter(engine: IRenderEngine, ht: HitTestInfo, defaultRenderer: Function) {
        const index = ht.pointIndex;
        const item = ht.item;
        if (index === 0) {
            engine.fill = WjCharts.GeneraliWaterfallChartStyle.intermediateTotal.fill;
            engine.stroke = WjCharts.GeneraliWaterfallChartStyle.intermediateTotal.stroke;
        } if (/delta/i.test(item.name)) {
            engine.fill = WjCharts.GeneraliWaterfallChartStyle.total.fill;
            engine.stroke = WjCharts.GeneraliWaterfallChartStyle.total.stroke;
        }

        defaultRenderer();
    }
}
